import { useEffect, useMemo, useRef, useState } from 'react';
import {
  formatDateTimeToDayAndMonth,
  getLocationAlias,
  getLocationHostSiteName,
  makeHrefToBuyTicketsForm,
  Trips,
} from '.';
import { DateTime } from 'luxon';
import { DEFAULT_TIMEZONE } from '../ui';
import { TIMES_OF_DAY_KEY } from '../trips';
import i18next from 'i18next';
import { useSearchParams } from 'react-router-dom';

const GET_TRIPS = process.env.REACT_APP_FILTER_QUERY;

const useGetTrips = () => {
  const [trips, setTrips] = useState<Trips>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [searchParams] = useSearchParams();
  const eventSourceRef = useRef<EventSource | null>(null);
  const sort = searchParams.get('sort');
  const day = searchParams.get('day');

  const updatedTrips = useMemo(() => {
    return sort === 'next_trip' || (day && sort !== 'price')
      ? trips.slice().sort((trip, nextTrip) => {
          if (!trip.next_trip.next_time && !nextTrip.next_trip.next_time) {
            return 0;
          }
          //если первый не существует — двигаем второй вверх
          if (!trip.next_trip.next_time) {
            return 1;
          }
          //если второй не существует — двигаем первый вверх
          if (!nextTrip.next_trip.next_time) {
            return -1;
          }
          //если оба существуют — сравниваем
          return trip.next_trip.next_time - nextTrip.next_trip.next_time;
        })
      : trips;
  }, [trips, sort]);

  useEffect(() => {
    setTrips([]);
    setIsLoading(true);

    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }
    eventSourceRef.current = new EventSource(
      '/api' + GET_TRIPS + '?' + searchParams.toString()
    );
    eventSourceRef.current.addEventListener('data', event => {
      const parsedData = JSON.parse(event.data);
      parsedData.next_trip.closestDateDay = null;
      if (parsedData.next_trip?.next_time) {
        parsedData.next_trip.closestDateDay = formatDateTimeToDayAndMonth(
          DateTime.fromSeconds(parsedData.next_trip?.next_time, {
            zone: DEFAULT_TIMEZONE,
          }),
          DEFAULT_TIMEZONE,
          parsedData[TIMES_OF_DAY_KEY],
          i18next.language
        );
      }

      parsedData.next_trip.times = parsedData.next_trip.times.map(
        (time: string) => {
          const href = makeHrefToBuyTicketsForm(
            parsedData.id,
            getLocationAlias(),
            getLocationHostSiteName()
          );

          return {
            href,
            time,
          };
        }
      );
      setTrips(prevTrips => [...prevTrips, parsedData]);
    });

    eventSourceRef.current.addEventListener('end', () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      setIsLoading(false);
    });

    eventSourceRef.current.onerror = err => {
      console.error('SSE error:', err);
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      setIsLoading(false);
      setIsError(true);
    };

    return () => {
      setIsLoading(false);
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, [searchParams]);

  return { trips: updatedTrips, isLoading, isError };
};

export default useGetTrips;
